import { useEffect, useRef, useState } from "react";
import fifthElement from "./assets/brand/fifth-element-purple.svg";
import crmIcon from "./assets/icons/crm.png";
import growthIcon from "./assets/icons/growth.png";
import salesIcon from "./assets/icons/sales.png";
import logoPurple from "./assets/logo/itm8-logo-purple.svg";
import logoWhite from "./assets/logo/itm8-logo-white.svg";
import type { Session } from "./auth.ts";
import { Message } from "./components/Message.tsx";
import { applyTheme, loadTheme, nextTheme, themeLabel, type Theme } from "./theme.ts";
import { useChat } from "./useChat.ts";

const ACTIONS = [
  {
    icon: crmIcon,
    title: "Ta fram läget",
    body: "Sammanfatta möten, mejl och affärer från de senaste 90 dagarna.",
    prompt: (c: string) => `Ta fram läget för ${c}: vad har hänt de senaste 90 dagarna i mejl, möten och Teams, och hur ser affärerna ut i Dynamics?`,
  },
  {
    icon: growthIcon,
    title: "Uppdatera kundplanen",
    body: "Jämför med kundplanen och föreslå vad som ska läggas till.",
    prompt: (c: string) => `Jämför det senaste om ${c} med kundplanen och föreslå uppdateringar per affärsområde.`,
  },
  {
    icon: salesIcon,
    title: "Förbered kundmöte",
    body: "Få en kort brief inför nästa möte med kunden.",
    prompt: (c: string) => `Förbered mig inför nästa möte med ${c}: viktigaste frågorna, öppna affärer och vad vi lovat senast.`,
  },
];

export function App({ session }: { session: Session }) {
  const { state, send, decide, resume, stop, reset } = useChat(session);
  const [draft, setDraft] = useState("");
  const [customer, setCustomer] = useState("");
  const [theme, setTheme] = useState<Theme>(loadTheme);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [state.turns]);

  const submit = (text: string) => {
    const message = text.trim();
    if (!message || state.busy) return;
    setDraft("");
    void send(message);
  };

  const empty = state.turns.length === 0;

  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            <img className="logo logo-light" src={logoPurple} alt="itm8" />
            <img className="logo logo-dark" src={logoWhite} alt="itm8" />
            <span className="brand-divider" aria-hidden="true" />
            <span className="brand-name">Salesm8 kundagent</span>
            {session.config.mock && <span className="badge">Demo</span>}
          </div>
          <div className="topbar-actions">
            {!empty && (
              <button className="btn btn-ghost btn-sm" onClick={reset} aria-label="Ny konversation">
                <span>
                  Ny<span className="hide-sm"> konversation</span>
                </span>
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => setTheme(nextTheme[theme])} aria-label={themeLabel[theme]} title={themeLabel[theme]}>
              {theme === "dark" ? "Mörkt" : theme === "light" ? "Ljust" : "Auto"}
            </button>
            <span className="user" title={session.userName}>
              {session.userName}
            </span>
          </div>
        </div>
      </header>

      <main className="main">
        {empty ? (
          <section className="welcome">
            <img className="fifth" src={fifthElement} alt="" aria-hidden="true" />
            <p className="overline">Work IQ · Dynamics 365 Sales · Kundplan</p>
            <h1>Vilken kund vill du jobba med?</h1>
            <p className="lead">
              Agenten letar i dina mejl, möten och affärer och håller kundplanen uppdaterad. Den använder bara dina egna
              behörigheter och skriver aldrig utan ditt godkännande.
            </p>
            <label className="field">
              <span>Kund</span>
              <input
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder="t.ex. Contoso AB"
                autoFocus
              />
            </label>
            <div className="actions">
              {ACTIONS.map((a) => (
                <button key={a.title} className="tile" disabled={!customer.trim()} onClick={() => submit(a.prompt(customer.trim()))}>
                  <span className="tile-icon">
                    <img src={a.icon} alt="" />
                  </span>
                  <span className="tile-title">{a.title}</span>
                  <span className="tile-body">{a.body}</span>
                  <span className="tile-cta">Starta →</span>
                </button>
              ))}
            </div>
          </section>
        ) : (
          <section className="thread">
            {state.turns.map((turn) => (
              <Message key={turn.id} turn={turn} busy={state.busy} onDecide={decide} onResume={resume} />
            ))}
            <div ref={endRef} />
          </section>
        )}
      </main>

      <footer className="composer-wrap">
        <form
          className="composer"
          onSubmit={(e) => {
            e.preventDefault();
            submit(draft);
          }}
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit(draft);
              }
            }}
            rows={1}
            placeholder={empty ? "Eller fråga fritt om en kund…" : "Skriv ett svar eller en ny fråga…"}
            aria-label="Meddelande till agenten"
          />
          {state.busy ? (
            <button type="button" className="btn btn-secondary" onClick={stop}>
              Stoppa
            </button>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={!draft.trim()}>
              Skicka →
            </button>
          )}
        </form>
        <p className="disclaimer">AI-genererat innehåll. Kontrollera fakta innan du delar dem med kunden.</p>
      </footer>
    </div>
  );
}
